/**
 * Where an auth round-trip should put you back.
 *
 * Every sign-in path used to hand Supabase `window.location.origin`, so
 * signing in from /account (or any other deep page) returned you to the
 * homepage with no memory of why you went there. That is the whole bug this
 * module exists to fix.
 *
 * It is an ALLOWLIST, not a sanitizer, and the reason is hosting rather than
 * security. Supabase sends the browser to `redirectTo` as a FRESH navigation,
 * so the target has to be a path our host actually serves, and this app has
 * NO catch-all: an unlisted path 404s. Returning somebody to one of those
 * would trade "you land on the homepage" for "you land on a 404", which is
 * strictly worse than the bug. A path we cannot prove is served falls back to
 * the origin, which is exactly the old behaviour.
 *
 * TWO different things make a path servable, which is what the first version
 * of this file got wrong. It read vercel.json alone and concluded /collection,
 * /checker and /support were 404s. They are not, and never were: all three
 * are in `src/seo/routeMeta.ts`, so the build emits `dist/<route>.html` and
 * Vercel serves it via `cleanUrls` - THE FILESYSTEM IS CHECKED BEFORE
 * vercel.json rewrites. Three real pages were excluded from the return path
 * on the strength of a comment nobody had checked against the deployed site.
 * (`curl -o /dev/null -w '%{http_code}'` settles it in one request.)
 *
 * /suggest-commander is the one that genuinely 404s - no rewrite AND no
 * routeMeta entry - and it is kept out here for that reason, not by omission.
 *
 * The allowlist doubles as the open-redirect guard: nothing that is not a
 * literal known path of ours can come out of here, so there is no
 * "//evil.com" reading of a pathname to worry about.
 *
 * KEEP IN SYNC WITH BOTH vercel.json AND src/seo/routeMeta.ts. A route added
 * to either one is not returnable until it is added here too, and a route
 * added HERE with neither behind it is a 404 waiting to happen. The tests
 * read both files and fail on an entry nothing serves.
 */

// Exact paths, each served either by its own `"destination": "/index"`
// rewrite or by a prerendered `dist/<route>.html` (a `routeMeta.ts` entry).
// EXPORTED so the deploy-config test can iterate the real table rather than
// its own copy of it. A duplicated list in the test would let a NEW entry
// added here escape the very invariant the test exists to hold.
export const RETURNABLE_EXACT: ReadonlySet<string> = new Set([
  '/bracket',
  '/build',
  '/rules',
  '/decks',
  '/explore',
  '/about',
  '/accuracy',
  '/privacy',
  '/terms',
  '/account',
  '/partners',
  // The Android scanner's hand-back. Its ?batch= is the whole point of
  // coming back here, which is why the query now survives (authReturnUrl).
  '/collection/scan-import',
  // The Android app's return after a Google Play purchase. Signing in from
  // there must land back on it so the activation can finish.
  '/pro/activated',
  // Prerendered, not rewritten. Serving is what matters, not which
  // mechanism does it.
  '/collection',
  '/checker',
  '/support',
])

// Prefixes, each backed by a `:path*` rewrite. A bare prefix with nothing
// after it is NOT matched here - `/decks` and `/partners` are in the exact
// set above, and `/admin` alone is not a route at all.
export const RETURNABLE_PREFIXES: readonly string[] = [
  '/decks/',
  '/partners/',
  '/admin/',
]

// Deliberately absent: `/d/:token` and `/u/:token`. Both rewrite to prerender
// handlers under /api rather than to /index, so an auth return landing there
// is served by something other than the SPA shell. Not worth the risk for two
// pages nobody signs in from.

export function isReturnablePath(pathname: string): boolean {
  if (RETURNABLE_EXACT.has(pathname)) return true
  return RETURNABLE_PREFIXES.some(
    (prefix) => pathname.startsWith(prefix) && pathname.length > prefix.length,
  )
}

// Return nonce for a sign-in the Watchtower teaser started. The teaser writes
// a "where to go after sign-in" marker carrying a nonce and passes that same
// nonce EXPLICITLY to the sign-in call it starts, so only that call's return
// URL carries `?cb_return=<nonce>`. The landing page follows the marker ONLY
// when the URL's nonce matches it, which ties the redirect to that one
// attempt: a cancelled or failed attempt leaves a marker no later visit can
// match. Supabase's own events cannot do this job: auth-js 2.99.3 emits
// SIGNED_IN for a session merely recovered from storage on page load
// (_recoverAndRefresh), not only for a real sign-in.
//
// Deliberately an ARGUMENT, never module state: an ambient nonce outlives the
// page that set it (leave /bracket with the sign-in modal open and nothing
// clears it), and every later ordinary sign-in would carry it. Callers that
// pass nothing get byte-identical URLs.
export const AUTH_RETURN_NONCE_PARAM = 'cb_return'
const NONCE_SHAPE = /^[A-Za-z0-9-]{8,64}$/
function validNonce(nonce: unknown): string | null {
  return typeof nonce === 'string' && NONCE_SHAPE.test(nonce) ? nonce : null
}

// The query keys carried back, per returnable route: an ALLOWLIST of the
// state each page actually reads, never "everything but a few". Anything else
// (Supabase's own params, a stale cb_return, tracking junk, whatever a link
// was decorated with) is dropped. Keys match EXACTLY, case and all, the way
// the pages read them (URLSearchParams.get), first value wins. `source`
// rides everywhere: it is the Android app's launch marker. So does `billing`,
// the billing-capable app build's marker: carried, never trusted here, since
// androidAppBillingCapable() still requires the native context to honor it.
// The device key is NOT in the query (it is in localStorage), so it never
// rides in an auth return.
// Prefixes cover the dynamic routes under them: /decks/:id and its editor
// keep `filter` (the originating tab, re-applied by the back button), and
// /admin/metrics reads its series filters. /partners/* reads nothing.
const RETURN_KEYS_ANY_ROUTE = ['source', 'billing']
const RETURN_KEYS_BY_ROUTE: Record<string, readonly string[]> = {
  '/collection/scan-import': ['batch'],
  '/collection': ['filter'],
  '/build': ['commander', 'rebuild', 'bracket'],
  '/bracket': ['deck', 'src'],
  '/decks': ['filter'],
  '/explore': ['tab', 'precons'],
  '/pro/activated': ['src'],
}
const RETURN_KEYS_BY_PREFIX: Record<string, readonly string[]> = {
  '/decks/': ['filter'],
  '/admin/': ['range', 'platform', 'identity'],
}

/** The homepage: served, and the Android app's launch page. */
export const HOME_PATH = '/'
/** The ONLY query keys a homepage return carries: the app's launch markers. */
export const HOME_RETURN_KEYS: readonly string[] = RETURN_KEYS_ANY_ROUTE

/** The allowlisted keys for `pathname`, exactly as the pages read them. */
export function returnQueryKeys(pathname: string): ReadonlySet<string> {
  if (pathname === HOME_PATH) return new Set(HOME_RETURN_KEYS)
  const keys = [...RETURN_KEYS_ANY_ROUTE, ...(RETURN_KEYS_BY_ROUTE[pathname] ?? [])]
  for (const [prefix, more] of Object.entries(RETURN_KEYS_BY_PREFIX)) {
    if (pathname.startsWith(prefix)) keys.push(...more)
  }
  return new Set(keys)
}

// A query longer than this is dropped whole rather than truncated: it would
// ride into every magic-link email (a /bracket?deck= share blob can be long).
const MAX_RETURN_QUERY = 512

/** The page's own allowlisted query; empty when nothing qualifies. */
function returnQuery(pathname: string, search: unknown): URLSearchParams {
  const out = new URLSearchParams()
  if (typeof search !== 'string' || search.length === 0) return out
  const allowed = returnQueryKeys(pathname)
  for (const [k, v] of new URLSearchParams(search)) {
    if (allowed.has(k) && !out.has(k)) out.set(k, v)
  }
  return out.toString().length > MAX_RETURN_QUERY ? new URLSearchParams() : out
}

/**
 * The absolute URL to hand Supabase as `redirectTo` / `emailRedirectTo`.
 *
 * Returns the bare origin whenever the current page is not one we can prove
 * is served. The homepage is served (prerender-home writes dist/index.html)
 * and IS the bare origin, so it returns there too, except that it keeps the
 * Android app's two launch markers, `source` and `billing`, and nothing
 * else (HOME_RETURN_KEYS). Without them a sign-in started on the app's
 * launch page (`/?source=twa&billing=1`) would come back unmarked, and a
 * return with empty sessionStorage could no longer tell it is in the app.
 * Never throws: a sign-in button must not be able to fail on a URL edge
 * case.
 */
export function authReturnUrl(loc: Location | URL = window.location, nonce?: string | null): string {
  try {
    const origin = loc.origin
    // Normalize FIRST, then allowlist. `new URL` collapses dot segments, so
    // checking the raw pathname could pass "/decks/../../elsewhere".
    const normalized = new URL(loc.pathname, origin)
    if (normalized.origin !== origin) return origin
    if (normalized.pathname === HOME_PATH) {
      // The markers only, never the nonce: the teaser's return marker
      // points at its own page, and a bare homepage return stays bare.
      const params = returnQuery(HOME_PATH, loc.search)
      const query = params.toString()
      return query ? `${origin}/?${query}` : origin
    }
    if (!isReturnablePath(normalized.pathname)) return origin

    // PATH AND QUERY, never the hash.
    //
    // The hash is Supabase's, and not by convention: auth-js 2.99.3 defaults
    // to `flowType: 'implicit'` (GoTrueClient.js:24), so the session tokens
    // come back IN THE FRAGMENT of whatever URL we name here. Ours would
    // collide with them.
    //
    // The query is ours and IS real page state (a commander on /build, a
    // filter on /decks, the scan batch on /collection/scan-import, where
    // dropping it sent a signed-in user's scan into a guest collection). It
    // is carried since 2026-10-19, but only the keys each route reads
    // (RETURN_KEYS_BY_ROUTE), and dropped whole past MAX_RETURN_QUERY. It
    // cannot move the redirect anywhere: the origin is ours and the path is
    // the allowlisted one, and URLSearchParams re-encodes every value.
    //
    // It works because the dashboard's Redirect URLs entry
    // `https://commanderbracket.app/**` does match a query-carrying URL - glob
    // metacharacters live in the PATTERN, and a `?` in the value being matched
    // is just a character. (Checked at review, 2026-08-24, after this comment
    // first claimed the opposite.)
    // Runtime-checked, not just typed: a handler wired straight to onClick
    // would hand a click event in here.
    const token = validNonce(nonce)
    const params = returnQuery(normalized.pathname, loc.search)
    if (token) params.set(AUTH_RETURN_NONCE_PARAM, token)
    const query = params.toString()
    return `${origin}${normalized.pathname}${query ? `?${query}` : ''}`
  } catch {
    // window.location is always well-formed in a browser; this is here so a
    // synthetic Location in a test or an embedded webview cannot break
    // sign-in.
    return typeof window !== 'undefined' ? window.location.origin : ''
  }
}
